import axios from 'axios';

// Shared helpers for chats: API headers, time formatting, retention labels, system messages, links

export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
export const dmApi = {
  get: (url) => axios.get(url, { headers: authHeaders() }),
  post: (url, body = {}) => axios.post(url, body, { headers: authHeaders() }),
  patch: (url, body = {}) => axios.patch(url, body, { headers: authHeaders() }),
  delete: (url) => axios.delete(url, { headers: authHeaders() }),
};

export const EDIT_WINDOW_MS = 15 * 60 * 1000;

export const RETENTION_OPTIONS = [
  { days: 7, label: '1 week', short: '1w' },
  { days: 14, label: '2 weeks', short: '2w' },
  { days: 30, label: '1 month', short: '1mo' },
  { days: 90, label: '3 months', short: '3mo' },
  { days: 180, label: '6 months', short: '6mo' },
];
export const retentionLabel = (days) => RETENTION_OPTIONS.find(o => o.days === days)?.label || `${days} days`;
export const retentionShort = (days) => RETENTION_OPTIONS.find(o => o.days === days)?.short || `${days}d`;

// Name shown for a user: own nickname for them, display name, username; deleted accounts have no user
export function displayName(user, nicknames = {}) {
  if (!user || !user.id) return 'Deleted user';
  return nicknames[user.id] || user.display_name || user.username || 'Deleted user';
}

// Sender of a message as a user object (null for deleted accounts)
export const senderOf = (msg) => (msg.sender_id
  ? { id: msg.sender_id, username: msg.sender_username, display_name: msg.sender_display_name, avatar: msg.sender_avatar }
  : null);

// "Anna", "Anna and Ben", "Anna, Ben and Chris"
export function joinNames(names) {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// Text for a system message; the acting user is the message's sender.
// Other users are referenced by id; their names come with the message (system.users).
export function systemText(msg, { meId, nicknames } = {}) {
  const actor = msg.sender_id === meId ? 'You' : displayName(senderOf(msg), nicknames);
  const s = msg.system || {};
  const nameOf = (id) => (id === meId ? 'you' : displayName(s.users?.[id] ? { id, ...s.users[id] } : null, nicknames));
  switch (s.event) {
    case 'retention':
      return `${actor} set messages to disappear after ${retentionLabel(s.days)}`;
    case 'group_created':
      return s.name ? `${actor} created the group "${s.name}"` : `${actor} created the group`;
    case 'member_added':
      return `${actor} added ${joinNames((s.user_ids || []).map(nameOf))}`;
    case 'member_removed':
      return `${actor} removed ${nameOf(s.user_id)}`;
    case 'member_left':
      return `${actor} left the group`;
    case 'renamed':
      return `${actor} renamed the group to "${s.name}"`;
    case 'name_removed':
      return `${actor} removed the group name`;
    case 'icon_changed':
      return `${actor} changed the group icon`;
    case 'icon_removed':
      return `${actor} removed the group icon`;
    case 'owner_changed': {
      const target = nameOf(s.user_id);
      return target === 'you' ? 'You are now the group owner' : `${target} is now the group owner`;
    }
    default:
      return `${actor} changed the chat`;
  }
}

// Name of a chat: the partner in 1:1 chats; for groups the group name or the first members
export function chatName(conv, { nicknames = {}, meId } = {}) {
  if (!conv) return '';
  if (conv.type !== 'group') return displayName(conv.partner, nicknames);
  if (conv.name) return conv.name;
  const others = (conv.members || []).filter(m => m.id !== meId);
  if (others.length === 0) return 'Empty group';
  const names = others.slice(0, 3).map(m => displayName(m, nicknames));
  const total = (conv.member_count ?? (others.length + 1)) - 1;
  return total > names.length ? `${names.join(', ')} and ${total - names.length} more` : joinNames(names);
}

export function sameDay(a, b) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export function formatDate(iso) {
  const d = new Date(iso), now = new Date();
  if (sameDay(d, now)) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

// Centered stamp between messages, iMessage style: "Today 14:32", "Monday 09:10", "Oct 3 at 18:05"
export function formatStamp(iso) {
  const d = new Date(iso), now = new Date();
  const time = formatTime(iso);
  const day = formatDate(iso);
  if (day === 'Today' || day === 'Yesterday') return { day, time };
  if (now - d < 6 * 86400000) return { day: d.toLocaleDateString('en-US', { weekday: 'long' }), time };
  return { day, time: `at ${time}` };
}

// "Read 14:32" today, otherwise "Read Yesterday" / "Read October 3"
export function readLabel(iso) {
  const day = formatDate(iso);
  return `Read ${day === 'Today' ? formatTime(iso) : day}`;
}

// 1-3 emojis and nothing else are shown large without a bubble; returns the count (0 = normal text)
const PICTOGRAPHIC = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
export function bigEmojiCount(text) {
  if (!text || text.length > 40 || typeof Intl.Segmenter === 'undefined') return 0;
  const parts = [...new Intl.Segmenter().segment(text.trim())].map(s => s.segment).filter(s => s.trim());
  const onlyEmoji = parts.length <= 3 && parts.every(p => PICTOGRAPHIC.test(p) && !LETTER_OR_DIGIT.test(p));
  return onlyEmoji ? parts.length : 0;
}

export function formatTime(iso) {
  const is12h = localStorage.getItem('time_format') === '12h';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: is12h });
}

// Short relative time for the chat list
export function formatListTime(iso) {
  const d = new Date(iso), now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'Now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return formatTime(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const STATUS_LABEL = { online: 'Online', away: 'Away', dnd: 'Do not disturb' };

const URL_SPLIT = /(https?:\/\/[^\s.,!?)"']+(?:[.,!?)"'][^\s.,!?)"']+)*)/g;
const URL_TEST = /^https?:\/\//;

// Message text with clickable links. Links open in a new tab and never trigger the long-press menu.
// mentionName (servers): turns <@id> into a highlighted @name; returns null for unknown ids.
export function renderContent(text, isMe, mentionName) {
  if (mentionName) {
    return text.split(/(<@\d{1,10}>)/g).map((part, i) => {
      const id = /^<@(\d{1,10})>$/.exec(part)?.[1];
      if (!id) return <span key={i}>{renderContent(part, isMe)}</span>;
      const name = mentionName(Number(id));
      return (
        <span key={i} className="rounded-sm px-0.5 font-semibold" style={{ background: 'rgba(155,93,229,0.28)', color: isMe ? '#fff' : 'var(--purple-light)' }}>
          @{name || 'unknown'}
        </span>
      );
    });
  }
  return text.split(URL_SPLIT).map((part, i) =>
    URL_TEST.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        className={`break-all underline ${isMe ? 'text-white' : 'text-purple-light'}`}
        style={isMe ? { color: 'rgba(255,255,255,0.92)' } : undefined}
      >
        {part}
      </a>
    ) : part
  );
}
